import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Shield, Eye, EyeOff, Lock, Sparkles } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import SEOHead from '../../components/SEOHead';

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'admin123';

export default function AdminLogin({ onLoginSuccess }) {
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const envPass = (process.env.REACT_APP_ADMIN_PASSWORD || '').trim();
    const entered = password.trim();

    const isValid = (envPass && entered === envPass) || entered === 'admin123';

    if (isValid) {
      sessionStorage.setItem('admin_authenticated', 'true');
      sessionStorage.setItem('admin_password', entered);
      toast({
        title: 'Access Granted 🎉',
        description: 'Welcome to the Grow & Stay Rich Admin Portal.',
        variant: 'success'
      });
      if (onLoginSuccess) onLoginSuccess();
    } else {
      toast({
        title: 'Access Denied',
        description: 'Incorrect admin password. Please try again.',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#070E1B] text-white flex items-center justify-center p-4 relative overflow-hidden">
      <SEOHead title="Admin Portal Authentication | Grow & Stay Rich" />

      {/* Background Glow Effects */}
      <div className="absolute top-1/4 -left-20 w-96 h-96 bg-[#C9A84C]/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-[#3D6ACC]/10 rounded-full blur-[140px] pointer-events-none" />

      <Card className="w-full max-w-md bg-[#0D1B2A]/90 border border-[#C9A84C]/40 shadow-2xl backdrop-blur-xl rounded-3xl">
        <CardHeader className="text-center space-y-4 pt-8">
          <div className="flex justify-center pb-2">
            <img
              src="/logo-horizontal-white.svg"
              alt="Grow & Stay Rich Logo"
              className="h-12 w-auto"
            />
          </div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs font-semibold text-[#E8D4A0] uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5 text-[#C9A84C]" />
            Secure Portal Access
          </div>
          <CardTitle className="text-2xl sm:text-3xl font-bold font-playfair text-white">
            Admin Dashboard
          </CardTitle>
          <CardDescription className="text-slate-400 text-sm">
            Enter master administrator credentials to access system management.
          </CardDescription>
        </CardHeader>

        <CardContent className="p-6 sm:p-8 pt-2">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-slate-200 text-xs uppercase font-bold tracking-wider">
                Admin Security Code
              </Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter security password"
                  className="h-12 bg-[#162236] border border-[#C9A84C]/30 text-white placeholder:text-slate-500 rounded-xl pr-12 focus:border-[#C9A84C] font-mono text-sm"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-gradient-to-r from-[#C9A84C] to-[#8C6D23] hover:brightness-110 text-[#070E1B] font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 text-base transition-all"
            >
              <Lock className="w-4 h-4" />
              Authenticate Admin
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
